import {
    isDate,
    parse as dateParse,
    isValid,
    differenceInCalendarDays,
    differenceInMinutes,
    differenceInDays,
    differenceInHours,
    differenceInCalendarWeeks,
    isToday,
    isYesterday,
    isThisHour,
    isThisWeek,
    lastDayOfWeek,
} from 'date-fns';
import { t } from '../../components/l10n/l10n';

const weekStartsOnMondays = { weekStartsOn: 1 };

const format = (date: Date | undefined, mask: string = 'dd.mm.yy'): string | undefined => {
    if (!date) {
        return undefined;
    }

    const value = isDate(date) ? date : dateParse(date);

    if (!isValid(value)) {
        return undefined;
    }

    const pad = v => (String(v).length === 1 ? `0${v}` : v);

    return mask
        .replace('dd', pad(value.getDate()))
        .replace('mm', pad(value.getMonth() + 1))
        .replace('yyyy', String(value.getFullYear()))
        .replace('yy', String(value.getFullYear()).slice(2));
};

export function formatISODate(date: Date): string | undefined {
    return format(date, 'yyyy-mm-dd');
}

export const parseFromISOString = (date: string): Date | undefined => {
    const tempDate = new Date(date);
    return isValid(tempDate) ? parseFromCustomString(format(new Date(date))) : undefined;
};

const parseFromCustomString = (date: string = '', mask: string = 'dd.mm.yy'): Date | undefined => {
    if (!date || !date.length) {
        return undefined;
    }

    const sanitizedMask = mask.split(/[^dmy]/);
    const dd = sanitizedMask.findIndex(x => x === 'dd');
    const mm = sanitizedMask.findIndex(x => x === 'mm');
    const yy = sanitizedMask.findIndex(x => x === 'yy' || x === 'yyyy');

    let day;
    let month;
    let year;

    const getFullYear = y => {
        if (y && y.length === 2) {
            const z = Number.parseInt(y, 10);

            return z > 50 ? `19${y}` : `20${y}`;
        }

        return y;
    };

    const inputParts = date.split(/\D/);

    if (inputParts.length === 1) {
        const fullDate = inputParts[0];
        const unseparatedMask = mask.replace(/[^dmy]/g, '');

        day = fullDate.slice(unseparatedMask.indexOf('d'), unseparatedMask.lastIndexOf('d') + 1);
        month = fullDate.slice(unseparatedMask.indexOf('m'), unseparatedMask.lastIndexOf('m') + 1);
        year = getFullYear(fullDate.slice(unseparatedMask.indexOf('y'), unseparatedMask.lastIndexOf('y') + 3)); // account for full years
    } else {
        day = inputParts[dd];
        month = inputParts[mm];
        year = getFullYear(inputParts[yy]);
    }

    day = Number.parseInt(day, 10);
    month = Number.parseInt(month, 10);
    year = Number.parseInt(year, 10);

    const currentDate = new Date();

    return new Date(
        /* year */ Object.is(year, NaN) ? currentDate.getFullYear() : year,
        /* month */ Object.is(month, NaN) ? currentDate.getMonth() : month - 1, // months are zero based in javascript, so subtract a day
        /* day */ Object.is(day, NaN) ? currentDate.getDate() : day,
        /* hours */ 12,
        /* minutes */ 0 // set to midday to avoid UTC offset causing dates to be mismatched server side
    );
};

const differenceInCalendarDaysToSunday = date => differenceInCalendarDays(lastDayOfWeek(date, weekStartsOnMondays), date);

const differenceInCalendarDaysToMonday = date => differenceInCalendarDays(lastDayOfWeek(date, weekStartsOnMondays), date) + 1;

export const getTimeAgo = (createdAt: Date) => {
    const now = new Date();
    if (isToday(createdAt)) {
        if (differenceInMinutes(now, createdAt) <= 5) {
            return t`NOTIFICATIONS_JUST_NOW`;
        }

        if (isThisHour(createdAt)) {
            return t`NOTIFICATIONS_LAST_HOUR`;
        }

        const hours = differenceInHours(now, createdAt);

        if (hours <= 5) {
            return hours === 1 ? t`NOTIFICATIONS_ONE_HOUR_AGO` : t(`NOTIFICATIONS_N_HOURS_AGO`, { HOURS: hours });
        }

        return t`NOTIFICATIONS_TODAY`;
    } else if (isYesterday(createdAt)) {
        return t`NOTIFICATIONS_YESTERDAY`;
    } else if (isThisWeek(createdAt, weekStartsOnMondays)) {
        return t(`NOTIFICATIONS_N_DAYS_AGO`, { DAYS: differenceInDays(now, createdAt) });
    } else {
        const weeks = differenceInCalendarWeeks(now, createdAt, weekStartsOnMondays);
        return weeks === 1 ? t`NOTIFICATIONS_ONE_WEEK_AGO` : t(`NOTIFICATIONS_N_WEEKS_AGO`, { WEEKS: weeks });
    }
};

export default {
    format,
    parseFromCustomString,
    parseFromISOString,
    differenceInCalendarDaysToSunday,
    differenceInCalendarDaysToMonday,
};
